import React from 'react';
import { Paperclip } from 'lucide-react';
import { formatInstant } from '../../lib/calendar-date';
import type { StampedFileRef } from '../../api/public';

/**
 * What the PUBLIC review page shows for a ticket approved by uploaded file.
 *
 * THERE IS NO DOWNLOAD LINK HERE, AND THAT IS THE POINT.
 *
 * The review route projects exactly two fields out of the `customer_stamp_file`
 * column — the filename and the timestamp — and deliberately omits `r2_key`,
 * `sha256`, `content_type` and `byte_size`. The page is unauthenticated: its
 * whole authorisation is a token in a URL that was emailed, and an emailed URL
 * ends up in forwarded threads, shared inboxes and mail-scanner logs. So the
 * bytes are not reachable from here by design, and this component renders the
 * RECEIPT rather than the document: the customer sent it, this is what arrived,
 * this is when. Do not add a fetch for the file to this page — the API has no
 * public route that would answer one, and inventing a guess at an R2 URL would
 * be both broken and exactly the mistake the omission exists to prevent.
 *
 * The office downloads the real file from `GET /tickets/:id/stamped-file`, which
 * is authenticated and company-scoped.
 *
 * `uploaded_at` is a genuine INSTANT (written as `NOW()` into jsonb, equal to
 * `customer_approved_at`), so it gets the instant formatter and not the
 * calendar-day one — an upload at 19:10 local in Alberta is stored as the next
 * day in UTC, and the calendar helper would print tomorrow's date on the
 * customer's own receipt. Absent renders as an em dash, never as a blank.
 */

export interface StampedFileNoticeProps {
  stampedFile: StampedFileRef;
}

/**
 * The zone this PUBLIC page reads an instant in: the viewer's own.
 *
 * Not the tenant's, and not by choice. The rule everywhere signed-in is
 * `companies.time_zone`, but this page belongs to no signed-in tenant — it
 * renders outside TenantZoneGate, and GET /ticket-approval/review/:token sends
 * no `time_zone` in its payload. `useTenantTimeZone()` here is null, and
 * honouring the rule would print "Uploaded —" on the customer's receipt where
 * a time used to be. So the receipt keeps the zone it always had until the API
 * sends the company's zone with the review payload; then pass that instead.
 * Null if the runtime cannot name a zone, which renders the em dash.
 */
function viewerTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

const StampedFileNotice: React.FC<StampedFileNoticeProps> = ({ stampedFile }) => {
  const when = formatInstant(stampedFile.uploaded_at, viewerTimeZone());

  return (
    <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="stamped-file-notice">
      <h3 className="font-bold text-gray-900 mb-3">Signed ticket received</h3>
      <div className="flex items-center gap-3">
        <Paperclip size={18} className="text-gray-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm text-gray-900 break-words" data-testid="stamped-file-name">
            {stampedFile.filename}
          </p>
          <p className="text-xs text-gray-500" data-testid="stamped-file-uploaded-at">
            Uploaded {when ?? '—'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StampedFileNotice;
